import type { ActionType, LifecycleState } from "@/core/tma";

export type CommandItemKind = "load" | "carrier" | "customer" | "action";
export type CommandInvocationKind = "navigate" | "stage_action";

export interface CommandLoadInput {
  id: string;
  tenantId: string;
  reference: string;
  lifecycleState: LifecycleState;
  customerName: string;
  carrierName: string;
  origin: { city: string; state: string };
  destination: { city: string; state: string };
}

export interface CommandItem {
  id: string;
  kind: CommandItemKind;
  title: string;
  subtitle: string;
  tokens: string[];
  href?: string;
  actionType?: ActionType;
  requiresApproval?: boolean;
}

const approvalRequiredActions = new Set<ActionType>([
  "share_customer_performance_report",
  "book_carrier",
  "tender_carrier",
  "approve_accessorial",
  "override_compliance",
  "create_load",
  "cancel_load",
  "split_load",
  "generate_spot_quote",
  "send_customer_quote",
  "apply_accessorial",
  "generate_rate_confirmation",
  "send_tender_email",
  "send_tender_edi204",
  "capture_carrier_acceptance",
  "generate_bol",
  "generate_customer_invoice",
  "run_carrier_settlement_batch",
  "apply_quick_pay_discount",
  "redirect_payment_to_factor",
  "place_carrier_payment_hold",
  "release_carrier_payment_hold",
  "start_detention_clock",
  "bill_tonu",
  "open_osd_claim",
  "open_cargo_claim",
  "open_customer_dispute",
  "record_noa",
  "add_carrier_to_dnu",
  "push_accounting_transaction",
]);

export function buildCommandItems(args: {
  loads: CommandLoadInput[];
  actions: ActionType[];
}): CommandItem[] {
  const loadItems = args.loads.map(loadToCommandItem);
  const carrierItems = uniqueEntityItems(args.loads, "carrier");
  const customerItems = uniqueEntityItems(args.loads, "customer");
  const actionItems = args.actions.map(actionToCommandItem);
  return [...loadItems, ...carrierItems, ...customerItems, ...actionItems];
}

export function filterCommandItems(items: CommandItem[], query: string, limit = 8): CommandItem[] {
  const terms = normalize(query).split(" ").filter(Boolean);
  if (terms.length === 0) return items.slice(0, limit);

  return items
    .map((item) => ({ item, score: scoreItem(item, terms) }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score || left.item.title.localeCompare(right.item.title))
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function invokeCommandItem(item: CommandItem): {
  kind: CommandInvocationKind;
  decision: "allow" | "approval_required";
  message: string;
  href?: string;
  actionType?: ActionType;
} {
  if (item.href) {
    return {
      kind: "navigate",
      decision: "allow",
      message: `Open ${item.title}`,
      href: item.href,
    };
  }

  return {
    kind: "stage_action",
    decision: item.requiresApproval ? "approval_required" : "allow",
    message: item.requiresApproval ? "Draft staged for reviewer approval" : "Low-risk action staged",
    actionType: item.actionType,
  };
}

function loadToCommandItem(load: CommandLoadInput): CommandItem {
  const lane = `${load.origin.city}, ${load.origin.state} to ${load.destination.city}, ${load.destination.state}`;
  return {
    id: `load:${load.id}`,
    kind: "load",
    title: load.reference,
    subtitle: `${lane} - ${load.lifecycleState.replaceAll("_", " ")}`,
    href: `/loads/${load.id}`,
    tokens: [
      load.reference,
      load.customerName,
      load.carrierName,
      load.origin.city,
      load.origin.state,
      load.destination.city,
      load.destination.state,
      load.lifecycleState,
    ],
  };
}

function uniqueEntityItems(loads: CommandLoadInput[], kind: "carrier" | "customer"): CommandItem[] {
  const seen = new Set<string>();
  return loads.flatMap((load) => {
    const name = kind === "carrier" ? load.carrierName : load.customerName;
    const id = `${kind}:${name.toLowerCase()}`;
    if (seen.has(id)) return [];
    seen.add(id);
    return [
      {
        id,
        kind,
        title: name,
        subtitle: kind === "carrier" ? `Carrier on ${load.reference}` : `Customer on ${load.reference}`,
        tokens: [name, load.reference, load.origin.city, load.destination.city],
      },
    ];
  });
}

function actionToCommandItem(actionType: ActionType): CommandItem {
  return {
    id: `action:${actionType}`,
    kind: "action",
    title: labelAction(actionType),
    subtitle: approvalRequiredActions.has(actionType) ? "Draft and hold" : "Low-risk staged action",
    actionType,
    requiresApproval: approvalRequiredActions.has(actionType),
    tokens: [actionType, labelAction(actionType)],
  };
}

function labelAction(actionType: ActionType): string {
  return actionType
    .split("_")
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

function scoreItem(item: CommandItem, terms: string[]): number {
  const haystack = normalize([item.title, item.subtitle, ...item.tokens].join(" "));
  return terms.reduce((score, term) => {
    if (normalize(item.title).startsWith(term)) return score + 8;
    if (haystack.includes(term)) return score + 3;
    if (fuzzyIncludes(haystack, term)) return score + 1;
    return -100;
  }, 0);
}

function fuzzyIncludes(haystack: string, needle: string): boolean {
  let index = 0;
  for (const character of haystack) {
    if (character === needle[index]) index += 1;
    if (index === needle.length) return true;
  }
  return false;
}

function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}
